import * as React from "react";
import axios from "axios";
// import Form from "react-bootstrap/Form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { countryListAlpha2 } from "./countries";
import classnames from "classnames";
import styles from "./style.module.scss";
import { Form, Row } from "react-bootstrap/esm/";

type AddressFormProps = {
  namespace: string;
  required: boolean;
  countryCode: string;
  streetAddress: string;
  city: string;
  cityArea: string;
  name: string;
  companyName: string;
  countryArea: string;
  sortingCode: string;
  postalCode: string;
};

function AddressForm({ countryCode: defaultCountryCode = "US", namespace = "address", ...props }: AddressFormProps) {
  const [countryCode, setCountryCode] = React.useState(defaultCountryCode);
  const [countryAreas, setCountryAreas] = React.useState([]);
  const [countryArea, setCountryArea] = React.useState(props.countryArea);
  const [waiting, setWaiting] = React.useState(false);
  const [data, setData] = React.useState<string[][]>([]);

  const updateCountry = () => {
    setWaiting(true);
    axios
      .get("/accounts/country_data.json", {
        params: {
          cc: countryCode,
        },
      })
      .then((res) => {
        setData(res.data["field_order"]);
        setCountryAreas(res.data["country_areas"]);
        setWaiting(false);
      });
  };

  React.useEffect(() => {
    if (countryCode !== undefined) {
      updateCountry();
    }
  }, [countryCode]);

  const FormFields = {
    Name: (
      <Form.Group controlId={`${namespace}_name`} className="mb-2">
        <Form.Label>Attention</Form.Label>
        <Form.Control type="text" name={`${namespace}[attention]`} defaultValue={props.name} />
      </Form.Group>
    ),
    CompanyName: (
      <Form.Group controlId={`${namespace}_company_name`} className="mb-2">
        <Form.Label>Company Name</Form.Label>
        <Form.Control type="text" name={`${namespace}[company_name]`} defaultValue={props.companyName} />
      </Form.Group>
    ),
    StreetAddress: (
      <Form.Group controlId={`${namespace}_street_address`} className="mb-2">
        <Form.Label>Street Address</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name={`${namespace}[street_address]`}
          defaultValue={props.streetAddress}
          required={props.required ? true : false}
        />
      </Form.Group>
    ),
    City: (
      <Form.Group controlId={`${namespace}_city`} className="mb-2">
        <Form.Label>City</Form.Label>
        <Form.Control
          type="text"
          name={`${namespace}[city]`}
          defaultValue={props.city}
          required={props.required ? true : false}
        />
      </Form.Group>
    ),
    CityArea: (
      <Form.Group controlId={`${namespace}_city_area`} className="mb-2">
        <Form.Label>City Area</Form.Label>
        <Form.Control type="text" name={`${namespace}[city_area]`} defaultValue={props.cityArea} />
      </Form.Group>
    ),
    CountryArea: (
      <Form.Group controlId={`${namespace}_country_area`} className="mb-2">
        <Form.Label>State (Country Area)</Form.Label>
        {/* note the value/onChange difference here.  bug: https://github.com/react-bootstrap/react-bootstrap/issues/2091 */}
        <Form.Select
          name={`${namespace}[country_area]`}
          value={countryArea}
          onChange={(e) => setCountryArea(e.target.value)}
        >
          <option value=""></option>
          {countryAreas.map(([key, value], idx) => {
            return (
              <option key={idx} value={key}>
                {value}
              </option>
            );
          })}
        </Form.Select>
      </Form.Group>
    ),
    SortingCode: (
      <Form.Group controlId={`${namespace}_sorting_code`} className="mb-2">
        <Form.Label>Sorting Code</Form.Label>
        <Form.Control type="text" name={`${namespace}[sorting_code]`} defaultValue={props.sortingCode} />
      </Form.Group>
    ),
    PostalCode: (
      <Form.Group controlId={`${namespace}_postal_code`} className="mb-2">
        <Form.Label>Postal Code</Form.Label>
        <Form.Control type="text" name={`${namespace}[postal_code]`} defaultValue={props.postalCode} />
      </Form.Group>
    ),
  };

  const ComponentMap = {
    Name: FormFields.Name,
    CompanyName: FormFields.CompanyName,
    StreetAddress: FormFields.StreetAddress,
    City: FormFields.City,
    CityArea: FormFields.CityArea,
    CountryArea: FormFields.CountryArea,
    SortingCode: FormFields.SortingCode,
    PostalCode: FormFields.PostalCode,
  };

  const Spinner = <span>{waiting && <FontAwesomeIcon icon="spinner" spin />}</span>;

  const Overlay = <React.Fragment>{waiting && <div className={styles.overlay} />}</React.Fragment>;

  const RenderFormFields = (
    <div className={classnames({ [styles.formFields]: true, [styles.waiting]: waiting })}>
      {data.map((line, idx_line) => {
        return (
          <Row key={idx_line}>
            {line.map((field, idx_field) => {
              return (
                <div key={idx_field} className="col">
                  {ComponentMap[field]}
                </div>
              );
            })}
          </Row>
        );
      })}
      {Overlay}
    </div>
  );

  return (
    <React.Fragment>
      <Form.Group controlId={`${namespace}_country_code`} className="mb-2">
        <Form.Label className="me-2">Country</Form.Label>
        {Spinner}
        <Form.Select
          name={`${namespace}[country_code]`}
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
        >
          <option value=""></option>
          {Object.entries(countryListAlpha2).map(([key, value], idx) => {
            return (
              <option key={idx} value={key}>
                {value}
              </option>
            );
          })}
        </Form.Select>
      </Form.Group>
      {RenderFormFields}
    </React.Fragment>
  );
}

export default AddressForm;
